import {
  SB_DEFAULT_PLACEMENT_ENUMS,
  SB_MODIFIER_ORDER_TIERS,
  SB_OTHER_THAN_TOP_WRITE_ENUMS,
  SB_PLACEMENT_ENUM_MATCHERS,
  SB_PLACEMENT_MODIFIER_LIMITS,
  SB_PLACEMENT_ROW_NAMES,
  SB_TOP_OF_SEARCH_WRITE_ENUMS,
} from "../../constants/placements.constants"
import { ISbPlacementRowForTable } from "../../types/sbPlacement.types"
import {
  IAudienceBidAdjustment,
  IBidAdjustmentByPlacement,
  ISbBidAdjustmentItem,
  ISbCampaignBidding,
  ISbPlacementRow,
} from "../../types/sbPlacementApi.types"

function clampModifier(placement: string, value: number): number {
  const min =
    placement === SB_PLACEMENT_ROW_NAMES.OTHER_THAN_TOP
      ? SB_PLACEMENT_MODIFIER_LIMITS.OTHER_THAN_TOP_MIN
      : SB_PLACEMENT_MODIFIER_LIMITS.TOP_OF_SEARCH_MIN

  return Math.min(Math.max(value, min), SB_PLACEMENT_MODIFIER_LIMITS.MAX)
}

export interface IBaseBidTarget {
  targetId: string | number
  limitedCPC: number | null
  bid: number | null
  spend?: number | null
}

export function computeBaseBid(targets: IBaseBidTarget[]): number | null {
  return weightedAverageBidOverUniqueTargets(targets)
}

export function computeCurrentBid(targets: IBaseBidTarget[]): number | null {
  return averageOverUniqueTargets(targets, (target) => target.bid)
}

function averageOverUniqueTargets(
  targets: IBaseBidTarget[],
  selectValue: (target: IBaseBidTarget) => number | null
): number | null {
  const valueByTargetId = new Map<string, number | null>()
  for (const target of targets) {
    const targetId = String(target.targetId)
    if (!valueByTargetId.has(targetId)) {
      valueByTargetId.set(targetId, selectValue(target))
    }
  }

  if (valueByTargetId.size === 0) {
    return null
  }

  let totalValue = 0
  valueByTargetId.forEach((value) => {
    totalValue += value ?? 0
  })

  return totalValue / valueByTargetId.size
}

function weightedAverageBidOverUniqueTargets(targets: IBaseBidTarget[]): number | null {
  const valueByTargetId = new Map<string, { bid: number | null; spend: number }>()

  for (const target of targets) {
    const targetId = String(target.targetId)
    const bid = selectFinalBidToApply(target)
    const spend = normalizeMetric(target.spend) ?? 0
    const existing = valueByTargetId.get(targetId)

    if (!existing) {
      valueByTargetId.set(targetId, { bid, spend })
      continue
    }

    valueByTargetId.set(targetId, {
      bid: existing.bid ?? bid,
      spend: existing.spend + spend,
    })
  }

  const values = [...valueByTargetId.values()].filter((target) => target.bid !== null)
  if (values.length === 0) {
    return null
  }

  const totalSpend = values.reduce((sum, target) => sum + target.spend, 0)
  if (totalSpend <= 0) {
    return null
  }

  return values.reduce((sum, target) => sum + (target.bid ?? 0) * target.spend, 0) / totalSpend
}

function normalizeMetric(value: number | null | undefined): number | null {
  return Number.isFinite(value) ? Number(value) : null
}

function selectFinalBidToApply(target: IBaseBidTarget): number | null {
  const finalBid = normalizeMetric(target.limitedCPC)
  if (finalBid !== null && finalBid > 0) {
    return finalBid
  }
  return normalizeMetric(target.bid)
}

export function resolveMaxPendingPlacementModifier(
  adjustment: ISbBidAdjustmentItem | null | undefined
): number | null {
  const percentages = [
    ...(adjustment?.placements ?? []).map((item) => item.percentage),
    ...(adjustment?.audiences ?? []).map((item) => item.percentage),
  ]
    .map((percentage) => normalizeMetric(percentage))
    .filter((value): value is number => value !== null)

  return percentages.length ? Math.max(...percentages) : null
}

export function buildBidAdjustmentItemFromBidding(
  campaignId: string,
  bidding: ISbCampaignBidding | null | undefined
): ISbBidAdjustmentItem {
  return {
    campaignId,
    placements: bidding?.bidAdjustmentsByPlacement ?? [],
    audiences: (bidding?.shopperCohortBidAdjustments ?? []).flatMap((cohort) =>
      (cohort.audienceSegments ?? []).map((segment) => ({
        audienceId: segment.audienceId,
        percentage: cohort.percentage,
      }))
    ),
  }
}

export function adjustTargetBidForEffectiveBid(
  finalBid: number | null,
  modifier: number | null,
  minBid: number
): number | null {
  const bid = normalizeMetric(finalBid)
  if (bid === null || bid <= 0) {
    return null
  }

  const effectiveModifier = modifier !== null && modifier > 0 ? modifier : null
  const factor = effectiveModifier === null ? 1 : 1 + effectiveModifier / 100
  if (factor <= 0) {
    return roundBid(bid)
  }

  const adjusted = bid / factor
  return roundBid(minBid > 0 ? Math.max(adjusted, minBid) : adjusted)
}

export function computeDeflatedCPC(
  limitedCPC: number | null,
  adjustment: ISbBidAdjustmentItem | null | undefined,
  minBid: number
): number | null {
  return adjustTargetBidForEffectiveBid(
    limitedCPC,
    resolveMaxPendingPlacementModifier(adjustment),
    minBid
  )
}

export function computePlacementBid(row: ISbPlacementRow): number | null {
  if (row.acos <= 0) {
    return null
  }
  return (row.cpc * row.targetAcos) / row.acos
}

export function roundBid(bid: number | null): number | null {
  return bid === null ? null : Number(bid.toFixed(2))
}

export function computeBidFromModifier(
  baseBid: number | null,
  modifier: number | null
): number | null {
  if (baseBid === null || modifier === null) {
    return null
  }
  return baseBid * (1 + modifier / 100)
}

// Caps the auto-SUGGESTED modifier by the row's own order count so tiny-sample
// placements cannot push a 900% modifier that deflates base bids.
// Manual editing in the UI is unaffected (still bounded by min/900 only).
export function maxSuggestedModifierForOrders(orders: number | null | undefined): number {
  const normalizedOrders =
    Number.isFinite(orders) && (orders as number) > 0 ? (orders as number) : 0

  for (const tier of SB_MODIFIER_ORDER_TIERS) {
    if (normalizedOrders <= tier.maxOrders) {
      return tier.maxModifier
    }
  }
  return SB_PLACEMENT_MODIFIER_LIMITS.MAX
}

function computeUncappedPlacementModifier(
  placement: string,
  placementBid: number | null,
  baseBid: number | null
): number | null {
  if (placementBid === null || baseBid === null || baseBid <= 0) {
    return null
  }
  return clampModifier(placement, Math.round((placementBid / baseBid - 1) * 100))
}

function computePlacementModifier(
  placement: string,
  placementBid: number | null,
  baseBid: number | null,
  orders: number | null | undefined
): number | null {
  const uncapped = computeUncappedPlacementModifier(placement, placementBid, baseBid)
  if (uncapped === null) {
    return null
  }
  return Math.min(uncapped, maxSuggestedModifierForOrders(orders))
}

function computeUncappedAudienceModifier(
  audienceBid: number | null,
  maxPlacementBid: number | null
): number | null {
  if (audienceBid === null || maxPlacementBid === null || maxPlacementBid <= 0) {
    return null
  }
  return clampModifier(
    SB_PLACEMENT_ROW_NAMES.AUDIENCE,
    Math.round((audienceBid / maxPlacementBid - 1) * 100)
  )
}

function computeAudienceModifier(
  audienceBid: number | null,
  maxPlacementBid: number | null,
  orders: number | null | undefined
): number | null {
  const uncapped = computeUncappedAudienceModifier(audienceBid, maxPlacementBid)
  if (uncapped === null) {
    return null
  }
  return Math.min(uncapped, maxSuggestedModifierForOrders(orders))
}

function maxDefinedBid(first: number | null, second: number | null): number | null {
  if (first === null) {
    return second
  }
  if (second === null) {
    return first
  }
  return Math.max(first, second)
}

export function findPlacementAdjustment(
  bidding: ISbCampaignBidding,
  placementRowName: string
): IBidAdjustmentByPlacement | null {
  const matchers = SB_PLACEMENT_ENUM_MATCHERS[placementRowName] ?? []
  const adjustments = bidding.bidAdjustmentsByPlacement ?? []

  return (
    adjustments.find((adjustment) =>
      matchers.some((matcher) => adjustment.placement.toUpperCase().includes(matcher))
    ) ?? null
  )
}

export function resolvePlacementModifier(
  bidding: ISbCampaignBidding,
  placementRowName: string
): number | null {
  if (placementRowName === SB_PLACEMENT_ROW_NAMES.AUDIENCE) {
    return resolveAudienceModifier(bidding)
  }

  return findPlacementAdjustment(bidding, placementRowName)?.percentage ?? null
}

export function resolveAudienceModifier(bidding: ISbCampaignBidding): number | null {
  const cohorts = bidding.shopperCohortBidAdjustments ?? []
  return cohorts.length ? cohorts[0].percentage : null
}

export function resolveAudienceId(bidding: ISbCampaignBidding): string | null {
  const cohorts = bidding.shopperCohortBidAdjustments ?? []
  const segments = cohorts[0]?.audienceSegments ?? []
  return segments[0]?.audienceId ?? null
}

export function hasAudienceChange(
  bidding: ISbCampaignBidding,
  selectedAudienceId: string | null
): boolean {
  if (selectedAudienceId === null) {
    return false
  }
  return selectedAudienceId !== resolveAudienceId(bidding)
}

export function hasConfiguredAudiences(bidding: ISbCampaignBidding): boolean {
  const cohorts = bidding.shopperCohortBidAdjustments ?? []
  return cohorts.some((cohort) => (cohort.audienceSegments ?? []).length > 0)
}

export function resolvePlacementEnum(
  bidding: ISbCampaignBidding,
  placementRowName: string
): string {
  return (
    findPlacementAdjustment(bidding, placementRowName)?.placement ??
    SB_DEFAULT_PLACEMENT_ENUMS[placementRowName] ??
    placementRowName
  )
}

export function findAllPlacementEnums(
  bidding: ISbCampaignBidding,
  placementRowName: string
): string[] {
  const matchers = SB_PLACEMENT_ENUM_MATCHERS[placementRowName] ?? []
  const adjustments = bidding.bidAdjustmentsByPlacement ?? []

  return adjustments
    .map((adjustment) => adjustment.placement)
    .filter((placement) => matchers.some((matcher) => placement.toUpperCase().includes(matcher)))
}

export function resolvePlacementEnumsForWrite(
  bidding: ISbCampaignBidding,
  placementRowName: string
): string[] {
  const existing = findAllPlacementEnums(bidding, placementRowName)
  if (existing.length) {
    return existing
  }

  return placementRowName === SB_PLACEMENT_ROW_NAMES.OTHER_THAN_TOP
    ? SB_OTHER_THAN_TOP_WRITE_ENUMS
    : SB_TOP_OF_SEARCH_WRITE_ENUMS
}

export function buildPlacementRowsForTable(
  placements: ISbPlacementRow[],
  bidding: ISbCampaignBidding,
  baseBid: number | null,
  currentBid: number | null
): ISbPlacementRowForTable[] {
  const enriched = placements.map((placementRow) => ({
    placementRow,
    isAudience: placementRow.placement === SB_PLACEMENT_ROW_NAMES.AUDIENCE,
    baseBid,
    placementBid: computePlacementBid(placementRow),
  }))

  const top = enriched.find(
    (item) => item.placementRow.placement === SB_PLACEMENT_ROW_NAMES.TOP_OF_SEARCH
  )
  const other = enriched.find(
    (item) => item.placementRow.placement === SB_PLACEMENT_ROW_NAMES.OTHER_THAN_TOP
  )
  const audience = enriched.find((item) => item.isAudience)

  const topUncappedModifier = computeUncappedPlacementModifier(
    SB_PLACEMENT_ROW_NAMES.TOP_OF_SEARCH,
    top?.placementBid ?? null,
    top?.baseBid ?? null
  )
  const topModifier = computePlacementModifier(
    SB_PLACEMENT_ROW_NAMES.TOP_OF_SEARCH,
    top?.placementBid ?? null,
    top?.baseBid ?? null,
    top?.placementRow.orders
  )
  const otherUncappedModifier = computeUncappedPlacementModifier(
    SB_PLACEMENT_ROW_NAMES.OTHER_THAN_TOP,
    other?.placementBid ?? null,
    other?.baseBid ?? null
  )
  const otherModifier = computePlacementModifier(
    SB_PLACEMENT_ROW_NAMES.OTHER_THAN_TOP,
    other?.placementBid ?? null,
    other?.baseBid ?? null,
    other?.placementRow.orders
  )
  const topCapBinds =
    topModifier !== null && topUncappedModifier !== null && topModifier < topUncappedModifier
  const otherCapBinds =
    otherModifier !== null &&
    otherUncappedModifier !== null &&
    otherModifier < otherUncappedModifier
  const topDisplayedBid = top
    ? roundBid(
        topCapBinds
          ? computeBidFromModifier(baseBid, topModifier)
          : resolveDisplayPlacementBid(
              top.placementBid,
              baseBid,
              topModifier ?? resolvePlacementModifier(bidding, SB_PLACEMENT_ROW_NAMES.TOP_OF_SEARCH)
            )
      )
    : null
  const otherDisplayedBid = other
    ? roundBid(
        otherCapBinds
          ? computeBidFromModifier(baseBid, otherModifier)
          : resolveDisplayPlacementBid(
              other.placementBid,
              baseBid,
              otherModifier ??
                resolvePlacementModifier(bidding, SB_PLACEMENT_ROW_NAMES.OTHER_THAN_TOP)
            )
      )
    : null
  const maxPlacementBid = maxDefinedBid(topDisplayedBid, otherDisplayedBid)
  const audienceUncappedModifier = computeUncappedAudienceModifier(
    audience?.placementBid ?? null,
    maxPlacementBid
  )
  const audienceModifier = computeAudienceModifier(
    audience?.placementBid ?? null,
    maxPlacementBid,
    audience?.placementRow.orders
  )
  const audienceCapBinds =
    audienceModifier !== null &&
    audienceUncappedModifier !== null &&
    audienceModifier < audienceUncappedModifier

  const cappedDisplayedBidByPlacement = new Map<string, number | null>()
  if (topCapBinds) {
    cappedDisplayedBidByPlacement.set(SB_PLACEMENT_ROW_NAMES.TOP_OF_SEARCH, topDisplayedBid)
  }
  if (otherCapBinds) {
    cappedDisplayedBidByPlacement.set(SB_PLACEMENT_ROW_NAMES.OTHER_THAN_TOP, otherDisplayedBid)
  }
  if (audienceCapBinds) {
    const audienceCappedBid = roundBid(computeBidFromModifier(baseBid, audienceModifier))
    if (audienceCappedBid !== null) {
      cappedDisplayedBidByPlacement.set(SB_PLACEMENT_ROW_NAMES.AUDIENCE, audienceCappedBid)
    }
  }

  const modifierByPlacement = new Map<string, number | null>([
    [SB_PLACEMENT_ROW_NAMES.TOP_OF_SEARCH, topModifier],
    [SB_PLACEMENT_ROW_NAMES.OTHER_THAN_TOP, otherModifier],
    [SB_PLACEMENT_ROW_NAMES.AUDIENCE, audienceModifier],
  ])

  return enriched.map(({ placementRow, isAudience, baseBid, placementBid }) => {
    const modifier =
      modifierByPlacement.get(placementRow.placement) ??
      resolvePlacementModifier(bidding, placementRow.placement)
    const currentModifier = resolvePlacementModifier(bidding, placementRow.placement)

    const cappedDisplayedBid = cappedDisplayedBidByPlacement.get(placementRow.placement)

    return {
      ...placementRow,
      baseBid: roundBid(baseBid),
      placementBid:
        cappedDisplayedBid !== undefined
          ? cappedDisplayedBid
          : roundBid(resolveDisplayPlacementBid(placementBid, baseBid, modifier)),
      modifier,
      currentModifier,
      currentPlacementBid: roundBid(currentBid),
      isAudience,
      isModifierEditable: !isAudience || hasConfiguredAudiences(bidding),
    }
  })
}

function resolveDisplayPlacementBid(
  placementBid: number | null,
  baseBid: number | null,
  modifier: number | null
): number | null {
  if (placementBid !== null) {
    return placementBid
  }
  return computeBidFromModifier(baseBid, modifier)
}

export function buildAudienceAdjustments(
  bidding: ISbCampaignBidding,
  percentage: number
): IAudienceBidAdjustment[] {
  const cohorts = bidding.shopperCohortBidAdjustments ?? []

  return cohorts.flatMap((cohort) =>
    (cohort.audienceSegments ?? []).map((segment) => ({
      audienceId: segment.audienceId,
      percentage,
    }))
  )
}

export function buildBidAdjustmentItem(
  campaignId: string,
  rows: ISbPlacementRowForTable[],
  bidding: ISbCampaignBidding,
  selectedAudienceId: string | null = null
): ISbBidAdjustmentItem {
  const placementRows = rows.filter((row) => !row.isAudience && row.modifier !== null)
  const audienceRow = rows.find((row) => row.isAudience && row.modifier !== null)

  return {
    campaignId,
    placements: placementRows.flatMap((row) =>
      resolvePlacementEnumsForWrite(bidding, row.placement).map((placement) => ({
        placement,
        percentage: row.modifier as number,
      }))
    ),
    audiences: buildAudiencesForWrite(bidding, audienceRow, selectedAudienceId),
  }
}

function buildAudiencesForWrite(
  bidding: ISbCampaignBidding,
  audienceRow: ISbPlacementRowForTable | undefined,
  selectedAudienceId: string | null
): IAudienceBidAdjustment[] {
  if (selectedAudienceId) {
    return [{ audienceId: selectedAudienceId, percentage: audienceRow?.modifier ?? undefined }]
  }

  return audienceRow ? buildAudienceAdjustments(bidding, audienceRow.modifier as number) : []
}

export function buildAudienceSelectionAdjustment(
  campaignId: string,
  existing: ISbBidAdjustmentItem | undefined,
  audienceId: string
): ISbBidAdjustmentItem {
  return {
    campaignId,
    placements: existing?.placements ?? [],
    audiences: [{ audienceId }],
  }
}

export function hasModifierChanges(
  rows: ISbPlacementRowForTable[],
  bidding: ISbCampaignBidding
): boolean {
  return rows.some((row) => row.modifier !== resolvePlacementModifier(bidding, row.placement))
}
